import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ScatterChart, Scatter, Cell
} from 'recharts';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chemistry color scheme
const CHEMISTRY_COLORS = {
  'NMC': '#f97316', // Orange
  'LFP': '#3b82f6', // Blue  
  'LTO': '#22c55e', // Green
  'LCO': '#a855f7'  // Purple
};

const METRIC_LABELS = {
  material_purity: 'Material Purity (%)',
  recovery_yield: 'Recovery Yield (%)',
  moisture_content: 'Moisture Content (%)',
  energy_intensity: 'Energy Intensity (kWh/kg)',
  li_content: 'Lithium Content (%)',
  co_content: 'Cobalt Content (%)',
  ni_content: 'Nickel Content (%)',
  mn_content: 'Manganese Content (%)',
  fe_content: 'Iron Content (%)',
  al_content: 'Aluminum Content (%)',
  cu_content: 'Copper Content (%)'
};

const BenchmarkingDashboard = ({ onViewBatch }) => {
  const [statistics, setStatistics] = useState({});
  const [comparisonData, setComparisonData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [filters, setFilters] = useState({
    chemistry: '',
    facility: '',
    date_from: '',
    date_to: '',
    metric: 'material_purity'
  });
  const [viewMode, setViewMode] = useState('comparison'); // comparison, trends, statistics
  const [loading, setLoading] = useState(true);

  // Fetch statistics data
  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/benchmarks/statistics`);
      setStatistics(response.data.statistics || {});
    } catch (error) {
      toast.error('Failed to load benchmark statistics');
      console.error('Statistics error:', error);
    }
  };

  // Fetch comparison data
  const fetchComparisonData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.chemistry) params.append('chemistry', filters.chemistry);
      if (filters.facility) params.append('facility', filters.facility);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axios.get(`${API}/benchmarks/comparison?${params.toString()}`);
      setComparisonData(response.data.comparison_data || []);
    } catch (error) {
      toast.error('Failed to load comparison data');
      console.error('Comparison data error:', error);
    }
  };

  // Fetch trend data
  const fetchTrendData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.chemistry) params.append('chemistry', filters.chemistry);
      params.append('metric', filters.metric);
      
      const response = await axios.get(`${API}/benchmarks/trends?${params.toString()}`);
      setTrendData(response.data.trend_data || []);
    } catch (error) {
      toast.error('Failed to load trend data');
      console.error('Trend data error:', error);
    }
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStatistics(),
        fetchComparisonData(),
        fetchTrendData()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Reload data when filters change
  useEffect(() => {
    fetchComparisonData();
    fetchTrendData();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Export chart as PNG
  const exportChart = (chartId, filename) => {
    // Implementation would use html2canvas or similar
    toast.success(`Export functionality for ${filename} - implementation pending`);
  };

  // Handle chart bar clicks - fetch full batch data from API
  const handleChartClick = async (data) => {
    if (!data || !data.batch_id) return;
    
    try {
      // Fetch the complete batch record from API
      const response = await axios.get(`${API}/batches/${data.batch_id}`);
      const fullBatch = response.data;
      
      if (fullBatch && onViewBatch) {
        onViewBatch(fullBatch);
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
      toast.error('Failed to load batch details');
    }
  };

  // Prepare radar chart data
  const prepareRadarData = () => {
    if (!comparisonData.length) return [];
    
    const radarData = comparisonData.slice(0, 50).map(batch => ({
      batch: batch.batch_id.slice(-6),
      material_purity: batch.material_purity,
      recovery_yield: batch.recovery_yield,
      moisture_content: 100 - batch.moisture_content, // Invert for better visualization
      li_content: batch.li_content,
      co_content: batch.co_content,
      ni_content: batch.ni_content,
      facility: batch.facility_name,
      chemistry: batch.battery_chemistry
    }));
    
    return radarData;
  };

  // Prepare metal content heatmap data
  const prepareMetalHeatmapData = () => {
    if (!comparisonData.length) return [];
    
    const metals = ['li_content', 'co_content', 'ni_content', 'mn_content', 'fe_content', 'al_content', 'cu_content'];
    const heatmapData = [];
    
    comparisonData.slice(0, 50).forEach((batch, index) => {
      metals.forEach((metal, metalIndex) => {
        heatmapData.push({
          batch: index,
          metal: metalIndex,
          value: batch[metal],
          batchId: batch.batch_id,
          metalName: METRIC_LABELS[metal],
          facility: batch.facility_name
        });
      });
    });
    
    return heatmapData;
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold">{`Batch: ${data.batch_id || label}`}</p>
          <p className="text-sm text-gray-600">{`Facility: ${data.facility_name || data.facility}`}</p>
          <p className="text-sm text-gray-600">{`Chemistry: ${data.battery_chemistry || data.chemistry}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{color: entry.color}}>
              {`${entry.name}: ${entry.value}${entry.name.includes('Content') || entry.name.includes('Purity') || entry.name.includes('Yield') ? '%' : ''}`}
            </p>
          ))}
          <p className="text-xs text-gray-500 mt-2 cursor-pointer hover:text-blue-600">
            Click to view details
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2" style={{borderColor: 'rgb(20, 140, 0)'}}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Benchmarking Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Data-driven visual comparison and statistical analysis across battery chemistry types
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => exportChart('main-chart', 'benchmark-analysis')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Export Charts
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters & Controls</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chemistry Type</label>
              <select
                value={filters.chemistry}
                onChange={(e) => handleFilterChange('chemistry', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none"
                style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              >
                <option value="">All Chemistry Types</option>
                <option value="NMC">NMC - Nickel Manganese Cobalt</option>
                <option value="LFP">LFP - Lithium Iron Phosphate</option>
                <option value="LTO">LTO - Lithium Titanate</option>
                <option value="LCO">LCO - Lithium Cobalt Oxide</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Facility</label>
              <input
                type="text"
                value={filters.facility}
                onChange={(e) => handleFilterChange('facility', e.target.value)}
                placeholder="Filter by facility..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none"
                style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none"
                style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none"
                style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric (for trends)</label>
              <select
                value={filters.metric}
                onChange={(e) => handleFilterChange('metric', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none"
                style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              >
                {Object.entries(METRIC_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm border">
            {[
              { key: 'comparison', label: 'Multi-Sample Comparison' },
              { key: 'trends', label: 'Trend Analysis' },
              { key: 'statistics', label: 'Statistical Summary' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                  viewMode === key
                    ? 'text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={viewMode === key ? {backgroundColor: 'rgb(20, 140, 0)'} : {}}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        {viewMode === 'comparison' && (
          <div className="space-y-6">
            {/* Radar Chart */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Multi-Parameter Performance Radar (50 Samples)</h3>
                <span className="text-sm text-gray-500">{comparisonData.length} total samples</span>
              </div>
              <div className="w-full" style={{ height: '450px', minHeight: '450px' }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={350}>
                  <RadarChart data={prepareRadarData()}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="batch" fontSize={10} />
                    <PolarRadiusAxis domain={[0, 100]} fontSize={8} />
                    <Radar 
                      name="Material Purity" 
                      dataKey="material_purity" 
                      stroke={CHEMISTRY_COLORS[filters.chemistry] || '#3b82f6'} 
                      fill={CHEMISTRY_COLORS[filters.chemistry] || '#3b82f6'} 
                      fillOpacity={0.2} 
                      strokeWidth={2}
                    />
                    <Radar 
                      name="Recovery Yield" 
                      dataKey="recovery_yield" 
                      stroke="#22c55e" 
                      fill="#22c55e" 
                      fillOpacity={0.1} 
                      strokeWidth={1}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Bar Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Material Purity Comparison */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Material Purity Comparison (Top 50 Samples)</h3>
                <div className="w-full" style={{ height: '450px', minHeight: '450px' }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={400}>
                    <BarChart data={comparisonData.slice(0, 50)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="batch_id" 
                        angle={-90}
                        textAnchor="end"
                        height={120}
                        interval={Math.max(0, Math.floor(comparisonData.length / 25))}
                        fontSize={8}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="material_purity" 
                        fill={CHEMISTRY_COLORS[filters.chemistry] || '#3b82f6'}
                        onClick={handleChartClick}
                        style={{ cursor: 'pointer' }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Recovery Yield Comparison */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recovery Yield Comparison (Top 50 Samples)</h3>
                <div className="w-full" style={{ height: '450px', minHeight: '450px' }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={400}>
                    <BarChart data={comparisonData.slice(0, 50)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="batch_id" 
                        angle={-90}
                        textAnchor="end"
                        height={120}
                        interval={Math.max(0, Math.floor(comparisonData.length / 25))}
                        fontSize={8}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="recovery_yield" 
                        fill="#22c55e"
                        onClick={handleChartClick}
                        style={{ cursor: 'pointer' }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Comprehensive Performance Scatter Plot */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Correlation Analysis (All 50 Samples)</h3>
              <div className="w-full" style={{ height: '450px', minHeight: '450px' }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={400} minHeight={350}>
                  <ScatterChart data={comparisonData.slice(0, 50)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      dataKey="material_purity" 
                      name="Material Purity (%)"
                      domain={[70, 100]}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="recovery_yield" 
                      name="Recovery Yield (%)"
                      domain={[70, 100]}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ strokeDasharray: '3 3' }}
                    />
                    <Legend />
                    <Scatter name="NMC" data={comparisonData.filter(d => d.battery_chemistry === 'NMC').slice(0, 50)} fill={CHEMISTRY_COLORS['NMC']} />
                    <Scatter name="LFP" data={comparisonData.filter(d => d.battery_chemistry === 'LFP').slice(0, 50)} fill={CHEMISTRY_COLORS['LFP']} />
                    <Scatter name="LTO" data={comparisonData.filter(d => d.battery_chemistry === 'LTO').slice(0, 50)} fill={CHEMISTRY_COLORS['LTO']} />
                    <Scatter name="LCO" data={comparisonData.filter(d => d.battery_chemistry === 'LCO').slice(0, 50)} fill={CHEMISTRY_COLORS['LCO']} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Analysis:</strong> This scatter plot shows the correlation between Material Purity and Recovery Yield across all chemistry types. Each point represents a batch sample, color-coded by chemistry type.</p>
              </div>
            </div>

            {/* Metal Content Distribution */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Metal Content Distribution (50 Samples)</h3>
              <div className="w-full" style={{ height: '500px', minHeight: '500px' }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={400} minHeight={450}>
                  <BarChart data={comparisonData.slice(0, 50)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="batch_id" 
                      angle={-90} 
                      textAnchor="end" 
                      height={140}
                      interval={Math.max(0, Math.floor(comparisonData.length / 20))}
                      fontSize={7}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="li_content" stackId="metals" fill="#ef4444" name="Lithium" />
                    <Bar dataKey="co_content" stackId="metals" fill="#3b82f6" name="Cobalt" />
                    <Bar dataKey="ni_content" stackId="metals" fill="#22c55e" name="Nickel" />
                    <Bar dataKey="mn_content" stackId="metals" fill="#f97316" name="Manganese" />
                    <Bar dataKey="fe_content" stackId="metals" fill="#a855f7" name="Iron" />
                    <Bar dataKey="al_content" stackId="metals" fill="#06b6d4" name="Aluminum" />
                    <Bar dataKey="cu_content" stackId="metals" fill="#f59e0b" name="Copper" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'trends' && (
          <div className="space-y-6">
            {/* Main Trend Chart */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Trend Analysis - {METRIC_LABELS[filters.metric]}
                </h3>
                <span className="text-sm text-gray-500">{trendData.length} data points (all samples)</span>
              </div>
              <div className="w-full" style={{ height: '450px', minHeight: '450px' }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={400} minHeight={350}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => new Date(date).toLocaleDateString()}
                      interval={Math.max(0, Math.floor(trendData.length / 10))}
                    />
                    <YAxis />
                    <Tooltip 
                      content={<CustomTooltip />}
                      labelFormatter={(date) => `Date: ${new Date(date).toLocaleDateString()}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke={CHEMISTRY_COLORS[filters.chemistry] || '#3b82f6'} 
                      strokeWidth={2}
                      dot={{ fill: CHEMISTRY_COLORS[filters.chemistry] || '#3b82f6', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 6, stroke: CHEMISTRY_COLORS[filters.chemistry] || '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Trend Analysis:</strong> This chart shows the temporal evolution of {METRIC_LABELS[filters.metric]} across all batch samples. Use the chemistry filter to focus on specific battery types.</p>
              </div>
            </div>

            {/* Trend Summary Statistics */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Trend Statistics Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {trendData.length > 0 ? Math.max(...trendData.map(d => d.value)).toFixed(1) : 0}%
                  </div>
                  <div className="text-sm text-blue-700">Peak Value</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {trendData.length > 0 ? (trendData.reduce((sum, d) => sum + d.value, 0) / trendData.length).toFixed(1) : 0}%
                  </div>
                  <div className="text-sm text-green-700">Average Value</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {trendData.length > 0 ? Math.min(...trendData.map(d => d.value)).toFixed(1) : 0}%
                  </div>
                  <div className="text-sm text-orange-700">Minimum Value</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {trendData.length}
                  </div>
                  <div className="text-sm text-purple-700">Total Samples</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'statistics' && (
          <div className="space-y-6">
            {Object.keys(statistics).length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                <p className="text-gray-500">No statistical data available</p>
              </div>
            ) : (
              Object.entries(statistics).map(([chemistry, stats]) => (
                <div key={chemistry} className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: CHEMISTRY_COLORS[chemistry] || '#gray' }}
                      ></div>
                      {chemistry} Battery Chemistry Statistics
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {Object.entries(stats).map(([metric, values]) => (
                      <div key={metric} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">
                          {METRIC_LABELS[metric] || metric}
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Mean:</span>
                            <span className="font-medium">{values.mean}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Min:</span>
                            <span className="font-medium">{values.min}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Max:</span>
                            <span className="font-medium">{values.max}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Std Dev:</span>
                            <span className="font-medium">{values.stdev}%</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-600">Samples:</span>
                            <span className="font-medium">{values.count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BenchmarkingDashboard;